import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useStore, Panel, Badge, Empty, Modal, Field, Flash, useFlash, plural,
} from '../components/ui.jsx';
import {
  memberById, membershipsFor, planById, bookingsForMember, occurrenceById,
  assignPlan, freezeMembership, unfreezeMembership, cancelMembership,
  resumeMembership, updateMember, cancel, today, getState,
} from '../data/store.js';
import { format } from '../lib/money.js';
import { isFrozenOn, PLAN_KINDS } from '../lib/entitlements.js';
import { upcomingCharges } from '../lib/billing.js';
import { BOOKING_STATUS } from '../lib/booking.js';
import { addDays } from '../lib/recurrence.js';

export default function MemberDetail() {
  const { memberId } = useParams();
  useStore();
  const [assigning, setAssigning] = useState(false);
  const [editing, setEditing] = useState(false);
  const [holding, setHolding] = useState(null);
  const [ending, setEnding] = useState(null);
  const [flash, setFlash] = useFlash();

  const member = memberById(memberId);
  if (!member) {
    return (
      <>
        <header className="topbar"><h1>Member not found</h1></header>
        <div className="page"><Empty><Link to="/members">Back to members</Link></Empty></div>
      </>
    );
  }

  const subs = membershipsFor(member.id);
  const bookings = bookingsForMember(member.id)
    .map((b) => ({ b, occ: occurrenceById(b.occurrenceId) }))
    .filter((x) => x.occ)
    .sort((a, x) => x.occ.startsAt.localeCompare(a.occ.startsAt));

  const upcoming = bookings.filter((x) => x.occ.startsAt >= new Date().toISOString());
  const history = bookings.filter((x) => x.occ.startsAt < new Date().toISOString()).slice(0, 12);
  const attended = bookings.filter((x) => x.b.status === BOOKING_STATUS.ATTENDED).length;

  return (
    <>
      <header className="topbar">
        <h1>{member.name}</h1>
        <span className="sub">{member.email} · joined {member.joinedOn}</span>
        <div className="spacer" />
        <button className="primary" onClick={() => setAssigning(true)}>Assign plan</button>
      </header>

      <div className="page">
        <Flash flash={flash} />

        <div className="split">
          <div className="stack">
            <Panel title="Memberships" bodyless>
              {subs.length === 0 ? <Empty>No plan on file.</Empty> : (
                <table className="data">
                  <thead>
                    <tr><th>Plan</th><th>Status</th><th>Period</th><th className="right">Actions</th></tr>
                  </thead>
                  <tbody>
                    {subs.map((s) => {
                      const plan = planById(s.planId);
                      const frozen = isFrozenOn(s, today());
                      const isPack = plan && [PLAN_KINDS.PACK, PLAN_KINDS.DROP_IN].includes(plan.kind);
                      return (
                        <tr key={s.id}>
                          <td>
                            <b>{plan?.name ?? s.planId}</b>
                            <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                              {isPack
                                ? `${plural(s.creditsRemaining ?? 0, 'credit', 'credits')} remaining`
                                : `${format(s.amountCents ?? plan?.amountCents ?? 0)} / ${s.interval ?? 'month'}`}
                            </div>
                            {isPack && s.creditsExpireOn && (
                              <div style={{ fontSize: 11.5, marginTop: 3 }}>
                                {s.creditsExpireOn < today()
                                  ? <Badge tone="bad">{`Expired ${s.creditsExpireOn}`}</Badge>
                                  : <span className="mono" style={{ color: 'var(--ink-3)' }}>expires {s.creditsExpireOn}</span>}
                              </div>
                            )}
                          </td>
                          <td>
                            <Badge status={frozen ? 'frozen' : s.status} />
                            {s.cancelScheduled && s.endDate && (
                              <div style={{ fontSize: 11.5, marginTop: 3 }}>
                                <Badge tone="warn">{`Ends ${s.endDate}`}</Badge>
                              </div>
                            )}
                          </td>
                          <td className="mono num" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                            {s.currentPeriodStart} &rarr; {s.currentPeriodEnd}
                            {(s.freezes || []).length > 0 && (
                              <div style={{ marginTop: 4 }}>
                                {s.freezes.map((f) => (
                                  <div key={f.id ?? f.from} className="row" style={{ gap: 6, marginTop: 2 }}>
                                    <span>hold {f.from} &rarr; {f.to || 'open'}</span>
                                    <button
                                      className="plain small"
                                      onClick={() => {
                                        unfreezeMembership(s.id, f.id);
                                        setFlash({ tone: 'good', message: 'Hold removed.' });
                                      }}
                                    >
                                      remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="right">
                            <div className="row" style={{ justifyContent: 'flex-end' }}>
                              {s.status === 'active' && !s.cancelScheduled && (
                                <button className="small" onClick={() => setHolding(s)}>Hold</button>
                              )}
                              {s.status === 'active' && (s.cancelScheduled
                                ? (
                                  <button
                                    className="small"
                                    onClick={() => {
                                      resumeMembership(s.id);
                                      setFlash({ tone: 'good', message: 'Cancellation called off.' });
                                    }}
                                  >
                                    Keep membership
                                  </button>
                                )
                                : <button className="small danger" onClick={() => setEnding(s)}>End</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Panel>

            <Panel title={`Upcoming bookings — ${upcoming.length}`} bodyless>
              {upcoming.length === 0 ? <Empty>Nothing booked.</Empty> : (
                <table className="data">
                  <thead><tr><th>Class</th><th>When</th><th>Status</th><th className="right">Actions</th></tr></thead>
                  <tbody>
                    {upcoming.map(({ b, occ }) => (
                      <tr key={b.id}>
                        <td><Link to={`/schedule/${encodeURIComponent(occ.id)}`}>{occ.name}</Link></td>
                        <td className="mono num" style={{ fontSize: 12 }}>
                          {new Intl.DateTimeFormat('en-US', { timeZone: occ.timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(new Date(occ.startsAt))}
                        </td>
                        <td>
                          <Badge status={b.status} />
                          {b.waitlistPosition ? <span className="mono num" style={{ color: 'var(--ink-3)' }}> #{b.waitlistPosition}</span> : null}
                        </td>
                        <td className="right">
                          <button
                            className="small danger"
                            onClick={() => {
                              const r = cancel(b.id);
                              setFlash(r.ok
                                ? { tone: 'good', message: r.feeCents ? `Cancelled — ${format(r.feeCents)} late fee applied.` : 'Cancelled.' }
                                : { tone: 'bad', message: r.reason });
                            }}
                          >
                            Cancel
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            <Panel title="Recent attendance" bodyless>
              {history.length === 0 ? <Empty>No history yet.</Empty> : (
                <table className="data">
                  <thead><tr><th>Class</th><th>Date</th><th className="right">Status</th></tr></thead>
                  <tbody>
                    {history.map(({ b, occ }) => (
                      <tr key={b.id}>
                        <td>{occ.name}</td>
                        <td className="mono num" style={{ color: 'var(--ink-3)' }}>{occ.date}</td>
                        <td className="right"><Badge status={b.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>

          <div className="stack">
            <Panel
              title="Profile"
              actions={<button className="small" onClick={() => setEditing(true)}>Edit</button>}
            >
              <dl className="kv">
                <dt>Status</dt><dd><Badge status={member.status} /></dd>
                <dt>Email</dt><dd className="mono" style={{ fontSize: 12.5 }}>{member.email || '—'}</dd>
                <dt>Phone</dt><dd className="mono" style={{ fontSize: 12.5 }}>{member.phone || '—'}</dd>
                <dt>Joined</dt><dd className="mono num">{member.joinedOn}</dd>
                <dt>Waiver</dt>
                <dd>
                  {member.waiverSignedAt
                    ? <Badge tone="good">signed</Badge>
                    : (
                      <button
                        className="small"
                        onClick={() => { updateMember(member.id, { waiverSignedAt: new Date().toISOString() }); setFlash({ tone: 'good', message: 'Waiver recorded.' }); }}
                      >
                        Record waiver
                      </button>
                    )}
                </dd>
                <dt>Emergency</dt>
                <dd>{member.emergencyContact ? `${member.emergencyContact.name} · ${member.emergencyContact.phone}` : '—'}</dd>
                <dt>Attended</dt><dd className="num">{plural(attended, 'class')}</dd>
              </dl>
            </Panel>

            <Notes member={member} onSaved={() => setFlash({ tone: 'good', message: 'Note saved.' })} />

            <UpcomingCharges subs={subs} />
          </div>
        </div>
      </div>

      {assigning && (
        <AssignPlan
          memberId={member.id}
          onClose={() => setAssigning(false)}
          onDone={(plan) => { setAssigning(false); setFlash({ tone: 'good', message: `${plan.name} assigned.` }); }}
        />
      )}

      {editing && (
        <EditMember
          member={member}
          onClose={() => setEditing(false)}
          onDone={() => { setEditing(false); setFlash({ tone: 'good', message: 'Member updated.' }); }}
        />
      )}

      {holding && (
        <HoldMembership
          membership={holding}
          onClose={() => setHolding(null)}
          onDone={(range) => {
            setHolding(null);
            setFlash({
              tone: 'good',
              message: `On hold from ${range.from}${range.to ? ` to ${range.to}` : ', with no end date'}.`,
            });
          }}
        />
      )}

      {ending && (
        <EndMembership
          membership={ending}
          onClose={() => setEnding(null)}
          onDone={(mode) => {
            setEnding(null);
            setFlash({
              tone: 'good',
              message: mode === 'now'
                ? 'Membership cancelled.'
                : `Ends ${ending.currentPeriodEnd}. They can train until then.`,
            });
          }}
        />
      )}
    </>
  );
}

/** Contact details change constantly and there was no way to change them. */
function EditMember({ member, onClose, onDone }) {
  const [form, setForm] = useState({
    name: member.name ?? '',
    email: member.email ?? '',
    phone: member.phone ?? '',
    status: member.status ?? 'active',
    emergencyName: member.emergencyContact?.name ?? '',
    emergencyPhone: member.emergencyContact?.phone ?? '',
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  function save() {
    const emergency = form.emergencyName.trim() || form.emergencyPhone.trim()
      ? { name: form.emergencyName.trim(), phone: form.emergencyPhone.trim() }
      : null;
    updateMember(member.id, {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      status: form.status,
      emergencyContact: emergency,
    });
    onDone();
  }

  return (
    <Modal
      title="Edit member"
      onClose={onClose}
      footer={(
        <button className="primary" disabled={!form.name.trim()} onClick={save}>Save</button>
      )}
    >
      <div className="form-grid">
        <Field label="Full name"><input value={form.name} onChange={set('name')} autoFocus /></Field>
        <Field label="Status">
          <select value={form.status} onChange={set('status')}>
            <option value="lead">Lead</option>
            <option value="active">Active</option>
            <option value="lapsed">Lapsed</option>
            <option value="archived">Archived</option>
          </select>
        </Field>
        <Field label="Email"><input type="email" value={form.email} onChange={set('email')} /></Field>
        <Field label="Phone"><input value={form.phone} onChange={set('phone')} /></Field>
        <Field label="Emergency contact"><input value={form.emergencyName} onChange={set('emergencyName')} /></Field>
        <Field label="Emergency phone"><input value={form.emergencyPhone} onChange={set('emergencyPhone')} /></Field>
      </div>
    </Modal>
  );
}

/**
 * Notes existed on every member, were seeded empty, and rendered nowhere. The
 * front desk writes things here that matter ("shoulder injury, no overhead"),
 * so it saves on blur rather than behind a button nobody presses.
 */
function Notes({ member, onSaved }) {
  const [text, setText] = useState(member.notes ?? '');

  return (
    <Panel title="Notes">
      <textarea
        rows={5}
        value={text}
        placeholder="Injuries, preferences, anything the next person on the desk should know…"
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          if ((member.notes ?? '') === text) return;
          updateMember(member.id, { notes: text });
          onSaved();
        }}
      />
    </Panel>
  );
}

const HOLD_PRESETS = [7, 14, 30, 60];

function HoldMembership({ membership, onClose, onDone }) {
  const [days, setDays] = useState(30);
  const [from, setFrom] = useState(today());
  const openEnded = days === 'open';
  const to = openEnded ? null : addDays(from, Number(days));

  return (
    <Modal
      title="Put this membership on hold"
      onClose={onClose}
      footer={(
        <button
          className="primary"
          onClick={() => onDone(freezeMembership(membership.id, { from, to }))}
        >
          Hold membership
        </button>
      )}
    >
      <div className="stack">
        <div className="form-grid">
          <Field label="Starting">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value || today())} />
          </Field>
          <Field label="For">
            <select value={days} onChange={(e) => setDays(e.target.value)}>
              {HOLD_PRESETS.map((d) => <option key={d} value={d}>{plural(d, 'day', 'days')}</option>)}
              <option value="open">No end date</option>
            </select>
          </Field>
        </div>
        <div className="notice info">
          {openEnded
            ? 'The hold runs until someone lifts it. Use this for medical leave or deployment, where there is no return date yet.'
            : `On hold from ${from} to ${to}. They cannot book during a hold.`}
        </div>
      </div>
    </Modal>
  );
}

/**
 * Ending a membership defaults to the end of the paid period. Cancelling on
 * the spot takes away time the member has already paid for, which is a refund
 * conversation, so it is available but never the default.
 */
function EndMembership({ membership, onClose, onDone }) {
  const [mode, setMode] = useState('period_end');
  const now = mode === 'now';

  return (
    <Modal
      title="End this membership"
      onClose={onClose}
      footer={(
        <button
          className={now ? 'primary danger' : 'primary'}
          onClick={() => {
            cancelMembership(membership.id, { atPeriodEnd: !now });
            onDone(now ? 'now' : 'period_end');
          }}
        >
          {now ? 'Cancel immediately' : 'Schedule the ending'}
        </button>
      )}
    >
      <div className="stack">
        <Field label="When">
          <select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="period_end">At the end of the paid period</option>
            <option value="now">Immediately</option>
          </select>
        </Field>
        <div className={now ? 'notice bad' : 'notice info'}>
          {now
            ? 'Access stops now, including the part of this period they have already paid for. You will probably owe them a refund.'
            : `They keep training until ${membership.currentPeriodEnd} and are not billed again. You can call this off any time before then.`}
        </div>
      </div>
    </Modal>
  );
}

function UpcomingCharges({ subs }) {
  const recurring = subs.filter((s) => s.status === 'active' && s.amountCents > 0);
  if (recurring.length === 0) return null;
  const charges = recurring.flatMap((s) => upcomingCharges({
    startDate: s.currentPeriodEnd, interval: s.interval || 'month', amountCents: s.amountCents, count: 3,
  }));
  return (
    <Panel title="Upcoming charges" bodyless>
      <table className="data">
        <thead><tr><th>Date</th><th className="right">Amount</th></tr></thead>
        <tbody>
          {charges.map((c, i) => (
            <tr key={`${c.date}-${i}`}>
              <td className="mono num">{c.date}</td>
              <td className="right mono num">{format(c.amountCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function AssignPlan({ memberId, onClose, onDone }) {
  const state = getState();
  const [planId, setPlanId] = useState('');
  const plan = planById(planId);

  return (
    <Modal
      title="Assign plan"
      onClose={onClose}
      footer={(
        <button
          className="primary"
          disabled={!planId}
          onClick={() => { assignPlan(memberId, planId); onDone(plan); }}
        >
          Assign
        </button>
      )}
    >
      <div className="stack">
        <Field label="Plan">
          <select value={planId} onChange={(e) => setPlanId(e.target.value)}>
            <option value="">Choose a plan…</option>
            {state.plans.filter((p) => p.active).map((p) => (
              <option key={p.id} value={p.id}>{p.name} — {format(p.amountCents)}</option>
            ))}
          </select>
        </Field>
        {plan && (
          <div className="notice info">
            {plan.kind === PLAN_KINDS.RECURRING_CAPPED && `Capped at ${plan.limit} per ${plan.limitPeriod}.`}
            {plan.kind === PLAN_KINDS.RECURRING_UNLIMITED && 'Unlimited within the billing period.'}
            {[PLAN_KINDS.PACK, PLAN_KINDS.DROP_IN].includes(plan.kind) && `${plural(plan.credits, 'credit', 'credits')}, expiring after ${plural(plan.expiryDays, 'day', 'days')}.`}
            {plan.programs?.length ? ` Limited to: ${plan.programs.join(', ')}.` : ''}
          </div>
        )}
      </div>
    </Modal>
  );
}
